import { useState, useEffect, useRef } from "react";
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, Cell, Tooltip,
} from "recharts";

const CALENDLY_URL        = "https://calendly.com/d/d3qf-vh2-5c8/your-ceo-benchmark-results-20-minute-review";
const PRIVACY_POLICY_URL = "https://chiefexecutivenetwork.com/privacy";
const CF_TURNSTILE_SITE_KEY = "0x4AAAAAAA7zaYLNugUtv2UQ";
const BENCHMARK_API_URL = process.env.REACT_APP_BENCHMARK_API_URL || "/api/benchmark";

const CATEGORIES = [
  { id:"strategy",     label:"Strategy",                             desc:"Enterprise direction, capital allocation, long-term priorities",       inOn:"ON", short:"Strategy"    },
  { id:"operations",   label:"Operations & Execution",               desc:"Running the business, performance management, processes, systems",     inOn:"IN", short:"Operations"  },
  { id:"sales_bd",     label:"Sales & Business Development",         desc:"Revenue generation, pipeline, key accounts, marketing, deal-making",   inOn:"IN", short:"Sales & BD"  },
  { id:"people",       label:"People & Org Leadership",              desc:"Talent, culture, leadership team, org design",                          inOn:"ON", short:"People & Org"},
  { id:"product_tech", label:"Product, Innovation & Technology",     desc:"Product strategy, R&D, digital/AI, transformation initiatives",        inOn:"ON", short:"Product/Tech"},
  { id:"ma",           label:"M&A & Partnerships",                   desc:"Acquisitions, alliances, joint ventures",                               inOn:"ON", short:"M&A"         },
  { id:"board",        label:"Board & Investor Relations",           desc:"Board engagement, shareholder/investor relationships, fundraising",     inOn:"ON", short:"Board/IR"    },
  { id:"legal_ext",    label:"Legal, Regulatory & External Affairs", desc:"Compliance, government, public policy, media, community",              inOn:"IN", short:"Legal/Ext"   },
  { id:"self",         label:"Personal Development & Reflection",    desc:"Coaching, peer learning, reading, strategic thinking time",            inOn:"ON", short:"Self/Learn"  },
];

const BENCHMARKS = {
  "$100M-$500M": { strategy:9.5,  operations:7.5, sales_bd:6.0, people:9.0, product_tech:5.5, ma:2.5, board:5.0,  legal_ext:4.5, self:6.0, hoursPerWeek:58 },
  "$500M-$1B":   { strategy:11.0, operations:6.0, sales_bd:5.0, people:9.5, product_tech:5.5, ma:3.5, board:7.5,  legal_ext:5.0, self:6.5, hoursPerWeek:59 },
  "$1B+":        { strategy:12.0, operations:5.0, sales_bd:4.0, people:9.5, product_tech:5.5, ma:4.5, board:10.0, legal_ext:6.5, self:7.0, hoursPerWeek:60 },
};
const REVENUE_TIERS = Object.keys(BENCHMARKS);
const OWN_TYPES = [
  "Family-Owned",
  "Private Equity-Backed",
  "Partnership",
  "Public",
  "Employee-Owned (ESOP)",
  "Sole Proprietorship",
  "Other Investor-Owned",
];
const AI_MATURITY_OPTIONS = [
  { value:"1", label:"We're not yet actively using AI" },
  { value:"2", label:"I use AI personally but it hasn't spread to the team" },
  { value:"3", label:"Several leaders use AI tools in their work" },
  { value:"4", label:"AI is embedded in how we operate — processes, decisions, workflows" },
  { value:"5", label:"AI is a strategic priority with dedicated investment and governance" },
];
const CURRENT_YEAR  = new Date().getFullYear();
const PRIOR_YEAR    = CURRENT_YEAR - 1;

const FONT       = "'Open Sans', sans-serif";
const FONT_SERIF = "'Cormorant Garamond', serif";

const NAVY      = "#0B1628";
const NAVY2     = "#152238";
const NAVY3     = "#1E3254";
const GOLD      = "#C9A84C";
const GOLD2     = "#E8C96A";
const GOLD_DARK = "#9A7A2E";
const CREAM     = "#F9F5EE";
const IN_COLOR  = "#B87333";
const ON_COLOR  = "#4A7FA5";
const SUCCESS   = "#2D9E6B";
const DANGER    = "#C0392B";

function rgba(hex,a){const r=parseInt(hex.slice(1,3),16),g=parseInt(hex.slice(3,5),16),b=parseInt(hex.slice(5,7),16);return `rgba(${r},${g},${b},${a})`;}
function useFadeIn(){const [v,setV]=useState(false);useEffect(()=>{const t=setTimeout(()=>setV(true),40);return()=>clearTimeout(t);},[]);return v;}
function sumHours(obj,filter){return parseFloat(CATEGORIES.filter(c=>filter?c.inOn===filter:true).reduce((s,c)=>s+(parseFloat(obj?.[c.id])||0),0).toFixed(1));}
function getNextTier(rev){const i=REVENUE_TIERS.indexOf(rev);return i>=0&&i<REVENUE_TIERS.length-1?REVENUE_TIERS[i+1]:null;}

const MAX_PER_CATEGORY = 80;
const MAX_TOTAL        = 120;
function clampHours(v){ return Math.min(Math.max(parseFloat(v)||0, 0), MAX_PER_CATEGORY); }

const GoldRule = ({my=24}) => (
  <div style={{display:"flex",alignItems:"center",gap:12,margin:`${my}px 0`}}>
    <div style={{flex:1,height:1,background:`linear-gradient(90deg,transparent,${rgba(GOLD,0.4)})`}}/>
    <div style={{width:4,height:4,borderRadius:"50%",background:GOLD,opacity:0.6}}/>
    <div style={{flex:1,height:1,background:`linear-gradient(90deg,${rgba(GOLD,0.4)},transparent)`}}/>
  </div>
);

function ProgressBar({step,total}){
  return(
    <div style={{display:"flex",gap:6,marginBottom:8}}>
      {Array.from({length:total}).map((_,i)=>(
        <div key={i} style={{height:2,flex:1,borderRadius:1,background:i<step?`linear-gradient(90deg,${GOLD_DARK},${GOLD})`:rgba(GOLD,0.15),transition:"background 0.5s"}}/>
      ))}
    </div>
  );
}

function StepLabel({step,total,label}){
  return(
    <div style={{display:"flex",justifyContent:"space-between",marginBottom:28}}>
      <span style={{fontSize:10,letterSpacing:"0.18em",textTransform:"uppercase",color:GOLD,fontFamily:FONT,opacity:0.8}}>{label}</span>
      <span style={{fontSize:10,color:rgba(CREAM,0.3),fontFamily:FONT}}>{step} / {total}</span>
    </div>
  );
}

function Field({label,value,onChange,placeholder,type="text"}){
  const [f,setF]=useState(false);
  return(
    <div>
      <label style={{display:"block",fontFamily:FONT,fontSize:10,letterSpacing:"0.12em",textTransform:"uppercase",color:rgba(GOLD,0.7),marginBottom:7}}>{label}</label>
      <input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} onFocus={()=>setF(true)} onBlur={()=>setF(false)}
        style={{width:"100%",padding:"11px 14px",border:`1px solid ${f?GOLD:rgba(GOLD,0.25)}`,borderRadius:6,fontFamily:FONT,fontSize:14,color:CREAM,outline:"none",boxSizing:"border-box",background:rgba(GOLD,0.04),transition:"border-color 0.2s"}}/>
    </div>
  );
}

function SelectField({label,value,onChange,options}){
  const [f,setF]=useState(false);
  return(
    <div>
      <label style={{display:"block",fontFamily:FONT,fontSize:10,letterSpacing:"0.12em",textTransform:"uppercase",color:rgba(GOLD,0.7),marginBottom:7}}>{label}</label>
      <select value={value} onChange={e=>onChange(e.target.value)} onFocus={()=>setF(true)} onBlur={()=>setF(false)}
        style={{width:"100%",padding:"11px 14px",border:`1px solid ${f?GOLD:rgba(GOLD,0.25)}`,borderRadius:6,fontFamily:FONT,fontSize:14,color:value?CREAM:rgba(CREAM,0.35),outline:"none",background:NAVY2,boxSizing:"border-box",transition:"border-color 0.2s"}}>
        <option value="">Select…</option>
        {options.map(o=><option key={o} value={o} style={{color:CREAM,background:NAVY2}}>{o}</option>)}
      </select>
    </div>
  );
}

function GoldButton({onClick,disabled,label}){
  const [h,setH]=useState(false);
  return(
    <button onClick={onClick} disabled={disabled} onMouseEnter={()=>setH(true)} onMouseLeave={()=>setH(false)}
      style={{width:"100%",padding:"15px 28px",background:disabled?rgba(GOLD,0.1):h?`linear-gradient(135deg,${GOLD2},${GOLD})`:`linear-gradient(135deg,${GOLD},${GOLD_DARK})`,color:disabled?rgba(CREAM,0.25):NAVY,border:"none",borderRadius:6,cursor:disabled?"not-allowed":"pointer",fontFamily:FONT,fontSize:14,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase",boxShadow:disabled?"none":`0 4px 24px ${rgba(GOLD,0.3)}`,transition:"all 0.2s"}}>
      {label}
    </button>
  );
}

function NavButtons({onBack,onNext,disabled,nextLabel}){
  return(
    <div style={{display:"flex",gap:12,marginTop:28}}>
      <button onClick={onBack} style={{padding:"14px 20px",background:"transparent",border:`1px solid ${rgba(GOLD,0.25)}`,borderRadius:6,cursor:"pointer",fontFamily:FONT,fontSize:13,color:rgba(CREAM,0.5),flexShrink:0}}>← Back</button>
      <div style={{flex:1}}><GoldButton onClick={onNext} disabled={disabled} label={nextLabel||"Continue"}/></div>
    </div>
  );
}

function TotalCounter({total,peer,peerLabel}){
  const diff  = peer ? parseFloat((total-peer).toFixed(1)) : 0;
  const overMax = total > MAX_TOTAL;
  const warn  = Math.abs(diff)>8 || overMax;
  return(
    <div style={{marginBottom:14}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:rgba(GOLD,0.07),borderRadius:8,padding:"12px 18px",border:`1px solid ${rgba(GOLD,warn?0.45:0.18)}`}}>
        <div>
          <div style={{fontFamily:FONT,fontSize:9,color:rgba(GOLD,0.55),textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:3}}>Running total</div>
          <div style={{fontFamily:FONT_SERIF,fontSize:26,fontWeight:600,color:warn?"#F59E0B":GOLD,lineHeight:1}}>{total} hrs/wk</div>
        </div>
        {peer&&(
          <div style={{textAlign:"right"}}>
            <div style={{fontFamily:FONT,fontSize:9,color:rgba(CREAM,0.35),textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:3}}>{peerLabel||"Peer avg"}</div>
            <div style={{fontFamily:FONT,fontSize:16,color:rgba(CREAM,0.5)}}>{peer} hrs/wk</div>
            {diff!==0&&<div style={{fontFamily:FONT,fontSize:11,color:warn?"#F59E0B":rgba(CREAM,0.35),marginTop:2}}>{diff>0?`+${diff}`:diff} vs peers</div>}
          </div>
        )}
      </div>
      {overMax&&(
        <div style={{marginTop:6,padding:"8px 14px",background:rgba(DANGER,0.1),border:`1px solid ${rgba(DANGER,0.3)}`,borderRadius:6}}>
          <span style={{fontFamily:FONT,fontSize:12,color:rgba(DANGER,0.9)}}>⚠ Total exceeds {MAX_TOTAL} hrs/week — please review your entries.</span>
        </div>
      )}
    </div>
  );
}

function HoursGrid({data,onChange,compareData}){
  const cols = compareData ? "1fr 88px 76px" : "1fr 88px";
  return(
    <div style={{marginBottom:12}}>
      <div style={{display:"grid",gridTemplateColumns:cols,gap:10,padding:"0 12px",marginBottom:8}}>
        <div/>
        <div style={{fontFamily:FONT,fontSize:9,color:rgba(GOLD,0.5),textTransform:"uppercase",letterSpacing:"0.1em",textAlign:"center"}}>Hrs / wk</div>
        {compareData&&<div style={{fontFamily:FONT,fontSize:9,color:rgba(GOLD,0.5),textTransform:"uppercase",letterSpacing:"0.1em",textAlign:"center"}}>{PRIOR_YEAR}</div>}
      </div>
      {CATEGORIES.map(cat=>{
        const val  = parseFloat(data?.[cat.id])||0;
        const cmp  = compareData ? (parseFloat(compareData?.[cat.id])||0) : null;
        const diff = cmp!==null ? parseFloat((val-cmp).toFixed(1)) : null;
        return(
          <div key={cat.id} style={{display:"grid",gridTemplateColumns:cols,gap:10,alignItems:"center",marginBottom:5,padding:"9px 12px",background:rgba(GOLD,0.03),borderRadius:6,borderLeft:`2px solid ${cat.inOn==="IN"?rgba(IN_COLOR,0.5):rgba(ON_COLOR,0.5)}`}}>
            <div>
              <div style={{fontFamily:FONT,fontSize:13,color:CREAM,fontWeight:500}}>{cat.label}</div>
              <div style={{fontFamily:FONT,fontSize:10,color:rgba(CREAM,0.35),marginTop:2}}>{cat.desc}</div>
            </div>
            <div style={{display:"flex",justifyContent:"center"}}>
              <input type="number" min="0" max={MAX_PER_CATEGORY} step="0.5"
                value={val||""} placeholder="0"
                onChange={e=>onChange(cat.id, clampHours(e.target.value))}
                style={{width:60,padding:"6px 8px",border:`1px solid ${rgba(GOLD,0.3)}`,borderRadius:5,fontFamily:FONT,fontSize:14,color:GOLD,textAlign:"center",outline:"none",background:rgba(NAVY,0.5),boxSizing:"border-box"}}/>
            </div>
            {compareData&&(
              <div style={{textAlign:"center"}}>
                <div style={{fontFamily:FONT,fontSize:12,color:rgba(CREAM,0.4)}}>{cmp}h</div>
                {diff!==null&&diff!==0&&<div style={{fontFamily:FONT,fontSize:10,color:diff>0?SUCCESS:DANGER,fontWeight:600}}>{diff>0?"+":""}{diff}</div>}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function Testimonial({quote,name,title,company}){
  return(
    <div style={{background:rgba(GOLD,0.04),border:`1px solid ${rgba(GOLD,0.18)}`,borderRadius:10,padding:"18px 20px",height:"100%",display:"flex",flexDirection:"column",boxSizing:"border-box"}}>
      <div style={{fontFamily:FONT_SERIF,fontSize:22,color:rgba(GOLD,0.45),lineHeight:0.4,marginBottom:8}}>&ldquo;</div>
      <p style={{fontFamily:FONT_SERIF,fontSize:14,color:rgba(CREAM,0.82),lineHeight:1.55,fontStyle:"italic",marginBottom:14,flex:1}}>{quote}</p>
      <div style={{fontFamily:FONT,fontSize:11,color:GOLD,fontWeight:600}}>{name}</div>
      <div style={{fontFamily:FONT,fontSize:10,color:rgba(CREAM,0.4),marginTop:2}}>{title}{company&&<>, {company}</>}</div>
    </div>
  );
}

function StepProfile({data,onChange,onNext}){
  const v = useFadeIn();
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email||"");
  const valid   = data.name && data.company && data.revenue && data.industry && emailOk;
  return(
    <div style={{opacity:v?1:0,transform:v?"translateY(0)":"translateY(12px)",transition:"all 0.4s ease"}}>
      <div style={{display:"inline-flex",alignItems:"center",gap:8,border:`1px solid ${rgba(GOLD,0.35)}`,padding:"5px 14px",borderRadius:3,marginBottom:20}}>
        <div style={{width:6,height:6,borderRadius:"50%",background:GOLD}}/>
        <span style={{fontFamily:FONT,fontSize:10,letterSpacing:"0.2em",textTransform:"uppercase",color:GOLD}}>CEO100 · Confidential Benchmark</span>
      </div>
      <h2 style={{fontFamily:FONT_SERIF,fontSize:34,color:CREAM,marginBottom:10,lineHeight:1.15,fontWeight:600}}>How does your time compare to peers growing through the $100M–$1B curve?</h2>
      <p style={{color:rgba(CREAM,0.5),marginBottom:28,fontFamily:FONT,fontSize:14,lineHeight:1.65}}>In 4 minutes, see exactly how you allocate time vs. confidential CEO100 peer benchmarks — and where your priorities diverge most from CEOs at the next revenue tier.</p>

      {/* Fields on the left, testimonials as lighter-weight supporting content
          on the right — keeps the form itself from reading as the whole page,
          which was making it feel like more friction than it is. Collapses to
          a single column (fields first) below 760px — see .step1-grid in the
          <style> block at the bottom of this file. */}
      <div className="step1-grid" style={{display:"grid",gap:32,alignItems:"start"}}>
        <div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(210px,1fr))",gap:14,marginBottom:14}}>
            <Field label="Your Name" value={data.name||""} onChange={v=>onChange("name",v)} placeholder="Jane Smith"/>
            <Field label="Company Name" value={data.company||""} onChange={v=>onChange("company",v)} placeholder="Acme Corp"/>
          </div>
          <div style={{marginBottom:14}}><SelectField label="Annual Revenue" value={data.revenue||""} onChange={v=>onChange("revenue",v)} options={REVENUE_TIERS}/></div>
          <div style={{marginBottom:14}}><SelectField label="Primary Industry" value={data.industry||""} onChange={v=>onChange("industry",v)} options={["Technology / IT","Financial Services","Professional Services","Manufacturing – Industrial","Manufacturing – Consumer Goods","Construction / Engineering","Media / Publishing","Wholesale / Distribution","Healthcare","Retail","Other"]}/></div>
          <div style={{marginBottom:14}}><SelectField label="Your Title" value={data.title||""} onChange={v=>onChange("title",v)} options={["CEO","President & CEO","Founder & CEO","Co-CEO","Managing Director","Executive Director","President","Other C-Suite"]}/></div>
          <div style={{marginBottom:14}}><SelectField label="Ownership Structure" value={data.ownership||""} onChange={v=>onChange("ownership",v)} options={OWN_TYPES}/></div>
          <div style={{marginBottom:18}}>
            <label style={{display:"block",fontFamily:FONT,fontSize:10,letterSpacing:"0.12em",textTransform:"uppercase",color:rgba(GOLD,0.7),marginBottom:9}}>Your Organisation's Relationship with AI</label>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {AI_MATURITY_OPTIONS.map(opt=>{
                const selected = data.aiMaturity === opt.value;
                return(
                  <label key={opt.value} style={{display:"flex",alignItems:"flex-start",gap:10,cursor:"pointer",padding:"10px 14px",borderRadius:6,border:`1px solid ${selected?rgba(GOLD,0.5):rgba(GOLD,0.15)}`,background:selected?rgba(GOLD,0.08):rgba(GOLD,0.02),transition:"all 0.15s"}}>
                    <input
                      type="radio"
                      name="aiMaturity"
                      value={opt.value}
                      checked={selected}
                      onChange={()=>onChange("aiMaturity",opt.value)}
                      style={{marginTop:2,flexShrink:0,accentColor:GOLD}}
                    />
                    <span style={{fontFamily:FONT,fontSize:13,color:rgba(CREAM,0.85),lineHeight:1.5}}>{opt.label}</span>
                  </label>
                );
              })}
            </div>
          </div>
          <GoldRule my={20}/>
          <p style={{fontFamily:FONT,fontSize:12,color:rgba(CREAM,0.4),marginBottom:14,lineHeight:1.55}}>Enter your work email to receive your personalized report.</p>
          <div style={{marginBottom:6}}><Field label="Work Email" value={data.email||""} onChange={v=>onChange("email",v)} placeholder="you@company.com" type="email"/></div>
          <p style={{fontFamily:FONT,fontSize:11,color:rgba(CREAM,0.25),marginBottom:24,lineHeight:1.6}}>
            🔒 Strictly confidential. Individual data is never shared or published.{" "}
            <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" style={{color:rgba(GOLD,0.5),textDecoration:"underline"}}>Privacy policy →</a>
          </p>
          <GoldButton disabled={!valid} onClick={onNext} label="Begin the Benchmark →"/>
        </div>

        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <Testimonial
            quote="The survey was short, and it prompted a valuable comparison between where I'm investing my energy today and where I want to focus going forward. It will help me shift more energy toward higher-leverage work like external partnerships and other revenue opportunities on the business."
            name="Anupam Martins" title="CEO" company="Excelligence Learning Corp."
          />
          <Testimonial
            quote="Seeing how my time breaks down helped me understand why I'm spending as much time as I am in HR and business development. It also showed me whether that investment aligns with my commitment to growth and developing my direct reports—and where I may need to spend more time or improve as a coach and leader."
            name="Michael Rodenberg" title="CEO" company="Nifco America Corp."
          />
        </div>
      </div>
    </div>
  );
}

function StepActual({data,onChange,onNext,onBack,profile}){
  const v       = useFadeIn();
  const total   = sumHours(data);
  const bm      = BENCHMARKS[profile.revenue]||BENCHMARKS["$100M-$500M"];
  const overMax = total > MAX_TOTAL;
  return(
    <div style={{opacity:v?1:0,transition:"opacity 0.35s ease"}}>
      <h2 style={{fontFamily:FONT_SERIF,fontSize:30,color:CREAM,marginBottom:6,lineHeight:1.2,fontWeight:600}}>Your Current Time Allocation</h2>
      <p style={{color:rgba(CREAM,0.5),marginBottom:6,fontFamily:FONT,fontSize:14}}>Enter actual hours per week, not percentages. Use a typical week — be candid.</p>
      <p style={{color:rgba(GOLD,0.6),marginBottom:16,fontFamily:FONT,fontSize:12}}>Peer CEOs at your revenue stage average <strong style={{color:GOLD}}>{bm.hoursPerWeek}h/week</strong>. Enter hours, not percentages — your total should reflect a real workweek.</p>
      <TotalCounter total={total} peer={bm.hoursPerWeek}/>
      <HoursGrid data={data} onChange={onChange}/>
      <NavButtons onBack={onBack} onNext={onNext}
        disabled={total<20||overMax}
        nextLabel={total<20?"Enter your hours":overMax?"Total too high — please review":"Continue"}/>
    </div>
  );
}

function StepDesired({data,onChange,onNext,onBack,profile}){
  const v          = useFadeIn();
  const total      = sumHours(data);
  const bm         = BENCHMARKS[profile.revenue]||BENCHMARKS["$100M-$500M"];
  const overMax    = total > MAX_TOTAL;
  // NOTE: this step intentionally does NOT receive the CEO's actual-hours answers.
  // It previously pre-filled every category with the actual value and showed it
  // beside each input, which anchored "ideal" answers to "actual" answers instead
  // of eliciting an independent judgment. Keep it independent — see briefing notes.
  return(
    <div style={{opacity:v?1:0,transition:"opacity 0.35s ease"}}>
      <h2 style={{fontFamily:FONT_SERIF,fontSize:30,color:CREAM,marginBottom:6,lineHeight:1.2,fontWeight:600}}>Your Ideal Allocation (next 12 months)</h2>
      <p style={{color:rgba(CREAM,0.5),marginBottom:6,fontFamily:FONT,fontSize:14}}>Without looking back at your last answers, think fresh: where would you ideally spend your time over the next 12 months?</p>
      <p style={{color:rgba(GOLD,0.6),marginBottom:16,fontFamily:FONT,fontSize:12}}>Peer CEOs at your revenue stage average <strong style={{color:GOLD}}>{bm.hoursPerWeek}h/week</strong>. Enter hours, not percentages — your total should reflect a real workweek.</p>
      <TotalCounter total={total} peer={bm.hoursPerWeek}/>
      <HoursGrid data={data} onChange={onChange}/>
      <NavButtons onBack={onBack} onNext={onNext}
        disabled={total<20||overMax}
        nextLabel={total<20?"Enter your ideal hours":overMax?"Total too high — please review":"Continue"}/>
    </div>
  );
}

function StepOpenSignals({data,onChange,onNext,onBack}){
  const v = useFadeIn();
  return(
    <div style={{opacity:v?1:0,transition:"opacity 0.35s ease"}}>
      <h2 style={{fontFamily:FONT_SERIF,fontSize:30,color:CREAM,marginBottom:8,lineHeight:1.2,fontWeight:600}}>Before You See Your Results</h2>
      <p style={{color:rgba(CREAM,0.5),marginBottom:28,fontFamily:FONT,fontSize:14,lineHeight:1.6}}>
        These sharpen your executive summary and make the debrief conversation more valuable.
      </p>
      <div style={{marginBottom:22}}>
        <label style={{display:"block",fontFamily:FONT,fontSize:13,fontWeight:500,color:rgba(CREAM,0.75),marginBottom:9,lineHeight:1.5}}>
          What one activity is consuming your time right now that <em style={{color:GOLD}}>shouldn't be</em>?
        </label>
        <textarea value={data.tooMuch||""} onChange={e=>onChange("tooMuch",e.target.value)} rows={3} placeholder="Be direct…"
          style={{width:"100%",padding:"12px 14px",border:`1px solid ${rgba(GOLD,0.25)}`,borderRadius:6,fontFamily:FONT,fontSize:14,color:CREAM,resize:"vertical",outline:"none",boxSizing:"border-box",background:rgba(GOLD,0.04),lineHeight:1.55}}/>
      </div>
      <div style={{marginBottom:28}}>
        <label style={{display:"block",fontFamily:FONT,fontSize:13,fontWeight:500,color:rgba(CREAM,0.75),marginBottom:9,lineHeight:1.5}}>
          What one thing are you <em style={{color:GOLD}}>not doing enough of</em> that would most move your business forward?
        </label>
        <textarea value={data.tooLittle||""} onChange={e=>onChange("tooLittle",e.target.value)} rows={3} placeholder="Be direct…"
          style={{width:"100%",padding:"12px 14px",border:`1px solid ${rgba(GOLD,0.25)}`,borderRadius:6,fontFamily:FONT,fontSize:14,color:CREAM,resize:"vertical",outline:"none",boxSizing:"border-box",background:rgba(GOLD,0.04),lineHeight:1.55}}/>
      </div>
      <NavButtons onBack={onBack} onNext={onNext} disabled={false} nextLabel="Continue →"/>
    </div>
  );
}

function InOnExplainer(){
  return(
    <div style={{background:rgba(GOLD,0.04),border:`1px solid ${rgba(GOLD,0.18)}`,borderRadius:10,padding:"18px 20px",marginBottom:24}}>
      <div style={{fontFamily:FONT,fontSize:9,letterSpacing:"0.18em",textTransform:"uppercase",color:rgba(GOLD,0.6),marginBottom:12}}>Framework · How to Read This Report</div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:16}}>
        {[
          {code:"IN",color:IN_COLOR,title:"Working IN the Business",body:"Day-to-day operations, execution, sales management, and running what exists. Necessary — but scales poorly with you personally."},
          {code:"ON",color:ON_COLOR,title:"Working ON the Business",body:"Strategy, people, M&A, board relationships, and decisions only you can make. As companies scale, this share grows."},
        ].map(s=>(
          <div key={s.code} style={{display:"flex",gap:12,alignItems:"flex-start"}}>
            <div style={{width:28,height:28,borderRadius:5,background:rgba(s.color,0.18),border:`1px solid ${rgba(s.color,0.5)}`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <span style={{fontFamily:FONT,fontSize:11,fontWeight:700,color:s.color}}>{s.code}</span>
            </div>
            <div>
              <div style={{fontFamily:FONT,fontSize:13,fontWeight:600,color:CREAM,marginBottom:4}}>{s.title}</div>
              <div style={{fontFamily:FONT,fontSize:12,color:rgba(CREAM,0.45),lineHeight:1.6}}>{s.body}</div>
            </div>
          </div>
        ))}
      </div>
      <div style={{marginTop:12,padding:"10px 14px",background:rgba(GOLD,0.06),borderRadius:6,border:`1px solid ${rgba(GOLD,0.12)}`}}>
        <span style={{fontFamily:FONT,fontSize:12,color:rgba(CREAM,0.45),lineHeight:1.6}}><strong style={{color:rgba(GOLD,0.8)}}>The key insight:</strong> CEOs navigating the $100M–$1B curve systematically shift time from IN to ON. The data below shows where your peers — and the tier above — have made that shift.</span>
      </div>
    </div>
  );
}

function StackedPeerChart({actual,bm,nbm,nt,showNextTier,revealPeer=true}){
  const activeBm        = showNextTier&&nbm ? nbm : bm;
  const activePeerLabel = showNextTier&&nbm ? `${nt} avg` : "Peer avg";
  const chartData = CATEGORIES.map(cat=>({
    name:cat.short, label:cat.label, inOn:cat.inOn,
    you: parseFloat(actual?.[cat.id])||0,
    peer:activeBm[cat.id]||0,
    gap: parseFloat(((parseFloat(actual?.[cat.id])||0)-(activeBm[cat.id]||0)).toFixed(1)),
  }));
  // When revealPeer is false (the free on-screen summary), only the CEO's own
  // hours are drawn — no peer line, no gap number, no peer tooltip row. The
  // exact peer/next-tier comparison is reserved for the gated debrief view.
  const CustomBar=(props)=>{
    const{x,y,width,height,payload}=props;
    if(!payload?.you||!height)return null;
    if(!revealPeer){
      return(<rect x={x} y={y} width={width} height={height} fill={payload.inOn==="IN"?IN_COLOR:ON_COLOR} fillOpacity={0.75} rx={3}/>);
    }
    const peerH=height*(payload.peer/Math.max(payload.you,0.1));
    const peerY=y+height-Math.min(peerH,height);
    return(<g><rect x={x} y={y} width={width} height={height} fill={payload.inOn==="IN"?IN_COLOR:ON_COLOR} fillOpacity={0.75} rx={3}/><line x1={x-4} y1={peerY} x2={x+width+4} y2={peerY} stroke={GOLD} strokeWidth={2}/>{Math.abs(payload.gap)>=0.5&&(<text x={x+width/2} y={y-6} textAnchor="middle" fill={payload.gap>0?SUCCESS:DANGER} fontSize={9} fontFamily={FONT} fontWeight="700">{payload.gap>0?`+${payload.gap}`:payload.gap}h</text>)}</g>);
  };
  return(
    <div>
      <div style={{height:240}}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{top:20,right:8,bottom:44,left:-8}} barCategoryGap="22%">
            <XAxis dataKey="name" tick={{fontFamily:FONT,fontSize:9,fill:rgba(CREAM,0.45)}} axisLine={false} tickLine={false} interval={0} angle={-35} textAnchor="end" height={50}/>
            <YAxis tick={{fontFamily:FONT,fontSize:9,fill:rgba(CREAM,0.3)}} axisLine={false} tickLine={false} unit="h"/>
            <Tooltip content={({active,payload})=>{
              if(!active||!payload?.length)return null;
              const d=payload[0]?.payload;if(!d)return null;
              return(<div style={{background:NAVY2,border:`1px solid ${rgba(GOLD,0.35)}`,borderRadius:8,padding:"10px 14px",minWidth:175}}>
                <div style={{fontFamily:FONT,fontSize:11,color:GOLD,marginBottom:6,fontWeight:600}}>{d.label}</div>
                <div style={{display:"flex",justifyContent:"space-between",gap:16,marginBottom:3}}><span style={{fontFamily:FONT,fontSize:11,color:rgba(CREAM,0.5)}}>You ({PRIOR_YEAR})</span><span style={{fontFamily:FONT,fontSize:11,color:CREAM,fontWeight:600}}>{d.you}h</span></div>
                {revealPeer&&(<>
                <div style={{display:"flex",justifyContent:"space-between",gap:16,marginBottom:3}}><span style={{fontFamily:FONT,fontSize:11,color:rgba(CREAM,0.5)}}>{activePeerLabel}</span><span style={{fontFamily:FONT,fontSize:11,color:GOLD}}>{d.peer}h</span></div>
                <div style={{height:1,background:rgba(GOLD,0.15),margin:"5px 0"}}/>
                <div style={{display:"flex",justifyContent:"space-between",gap:16}}><span style={{fontFamily:FONT,fontSize:11,color:rgba(CREAM,0.5)}}>Gap</span><span style={{fontFamily:FONT,fontSize:12,fontWeight:700,color:d.gap>0?SUCCESS:d.gap<0?DANGER:rgba(CREAM,0.5)}}>{d.gap>0?`+${d.gap}`:d.gap}h</span></div>
                </>)}
              </div>);
            }}/>
            <Bar dataKey="you" shape={<CustomBar/>}/>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div style={{display:"flex",flexWrap:"wrap",gap:14,justifyContent:"center",marginTop:2}}>
        {[{el:<div style={{width:22,height:9,borderRadius:2,background:IN_COLOR,opacity:0.75}}/>,l:"IN (you)"},{el:<div style={{width:22,height:9,borderRadius:2,background:ON_COLOR,opacity:0.75}}/>,l:"ON (you)"},...(revealPeer?[{el:<svg width="22" height="8"><line x1="0" y1="4" x2="22" y2="4" stroke={GOLD} strokeWidth="2"/></svg>,l:activePeerLabel}]:[])].map((x,i)=>(
          <div key={i} style={{display:"flex",alignItems:"center",gap:5}}>{x.el}<span style={{fontFamily:FONT,fontSize:10,color:rgba(CREAM,0.4)}}>{x.l}</span></div>
        ))}
      </div>
    </div>
  );
}

function SplitBarRow({label,sublabel,inH,onH,maxH=65,highlight,aspirational}){
  const inW=Math.min((inH/maxH)*100,100),onW=Math.min((onH/maxH)*100,100),remW=Math.max(0,100-inW-onW);
  return(
    <div style={{display:"grid",gridTemplateColumns:"clamp(100px,27%,165px) 1fr clamp(70px,16%,108px)",gap:"8px 12px",alignItems:"center",marginBottom:10}}>
      <div>
        <div style={{fontFamily:FONT,fontSize:13,fontWeight:highlight?600:400,color:highlight?CREAM:rgba(CREAM,0.6)}}>{label}</div>
        {sublabel&&<div style={{fontFamily:FONT,fontSize:10,color:rgba(CREAM,0.3),marginTop:1}}>{sublabel}</div>}
      </div>
      <div style={{height:highlight?46:30,borderRadius:4,overflow:"hidden",display:"flex",boxShadow:highlight?`0 2px 18px ${rgba(GOLD,0.2)}`:"none",border:aspirational&&!highlight?`1px dashed ${rgba(GOLD,0.25)}`:"none",minWidth:0}}>
        <div style={{width:`${inW}%`,background:highlight?IN_COLOR:rgba(IN_COLOR,0.4),display:"flex",alignItems:"center",justifyContent:"center",transition:"width 0.7s ease"}}>{inW>=14&&<span style={{fontFamily:FONT,fontSize:highlight?12:10,fontWeight:700,color:"white"}}>{inH}h</span>}</div>
        <div style={{width:`${onW}%`,background:highlight?ON_COLOR:rgba(ON_COLOR,0.45),display:"flex",alignItems:"center",justifyContent:"center",transition:"width 0.7s ease"}}>{onW>=14&&<span style={{fontFamily:FONT,fontSize:highlight?12:10,fontWeight:700,color:"white"}}>{onH}h</span>}</div>
        {remW>0.5&&<div style={{width:`${remW}%`,background:"rgba(255,255,255,0.04)"}}/>}
      </div>
      <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
        <div style={{textAlign:"center"}}><div style={{fontFamily:FONT_SERIF,fontSize:highlight?17:13,fontWeight:600,color:highlight?IN_COLOR:rgba(IN_COLOR,0.6)}}>{inH}h</div><div style={{fontFamily:FONT,fontSize:8,color:rgba(CREAM,0.25),letterSpacing:"0.08em"}}>IN</div></div>
        <div style={{textAlign:"center"}}><div style={{fontFamily:FONT_SERIF,fontSize:highlight?17:13,fontWeight:600,color:highlight?ON_COLOR:rgba(ON_COLOR,0.6)}}>{onH}h</div><div style={{fontFamily:FONT,fontSize:8,color:rgba(CREAM,0.25),letterSpacing:"0.08em"}}>ON</div></div>
      </div>
    </div>
  );
}

function GapDotRow({cat,showNextTier,bm,nbm}){
  const activeBm=showNextTier&&nbm?nbm:bm;
  const color=cat.inOn==="IN"?IN_COLOR:ON_COLOR;
  const isPos=cat.gap>0;
  const peerVal=activeBm[cat.id]||0;
  const peerGapVsActual=parseFloat((cat.actual-peerVal).toFixed(1));
  const maxVal=Math.max(cat.actual,cat.desired,peerVal,1)*1.15;
  const pct=v=>Math.min((v/maxVal)*100,100);
  return(
    <div style={{marginBottom:8,padding:"13px 16px",background:rgba(GOLD,0.03),borderRadius:8,borderLeft:`2px solid ${rgba(color,0.5)}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:8}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontFamily:FONT,fontSize:13,color:CREAM,fontWeight:500}}>{cat.label}</span>
          <span style={{fontFamily:FONT,fontSize:9,color,background:rgba(color,0.12),padding:"1px 6px",borderRadius:3,fontWeight:600,letterSpacing:"0.06em"}}>{cat.inOn}</span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
          {cat.gap!==0&&(
            <div style={{display:"flex",alignItems:"center",gap:5,padding:"3px 10px",borderRadius:20,background:isPos?rgba(SUCCESS,0.15):rgba(DANGER,0.15),border:`1px solid ${isPos?rgba(SUCCESS,0.35):rgba(DANGER,0.35)}`}}>
              <span style={{fontSize:10}}>{isPos?"▲":"▼"}</span>
              <span style={{fontFamily:FONT,fontSize:11,fontWeight:700,color:isPos?SUCCESS:DANGER}}>{isPos?"MORE":"LESS"} · {Math.abs(cat.gap)}h/wk desired</span>
            </div>
          )}
          {cat.gap===0&&<span style={{fontFamily:FONT,fontSize:11,color:rgba(CREAM,0.3)}}>No change</span>}
          {peerGapVsActual!==0&&<span style={{fontFamily:FONT,fontSize:11,color:peerGapVsActual>0?rgba(SUCCESS,0.7):rgba(DANGER,0.7)}}>{peerGapVsActual>0?`+${peerGapVsActual}`:peerGapVsActual}h vs peers</span>}
        </div>
      </div>
      <div style={{position:"relative",height:36,marginBottom:4}}>
        <div style={{position:"absolute",top:"50%",left:"2%",right:"2%",height:2,background:rgba(CREAM,0.08),borderRadius:1,transform:"translateY(-50%)"}}/>
        <div style={{position:"absolute",left:`${pct(peerVal)}%`,top:"50%",transform:"translate(-50%,-50%)",zIndex:1}}>
          <div style={{width:2,height:22,background:GOLD,borderRadius:1,margin:"0 auto"}}/>
          <div style={{fontFamily:FONT,fontSize:8,color:rgba(GOLD,0.8),textAlign:"center",marginTop:3,whiteSpace:"nowrap"}}>{showNextTier&&nbm?"Next tier":"Peers"} {peerVal}h</div>
        </div>
        {cat.desired!==cat.actual&&(
          <div style={{position:"absolute",left:`${pct(cat.desired)}%`,top:"50%",transform:"translate(-50%,-50%)",zIndex:2}}>
            <div style={{width:14,height:14,borderRadius:"50%",border:`2px solid ${isPos?SUCCESS:DANGER}`,background:NAVY,display:"flex",alignItems:"center",justifyContent:"center"}}>
              <div style={{width:5,height:5,borderRadius:"50%",background:isPos?SUCCESS:DANGER}}/>
            </div>
          </div>
        )}
        <div style={{position:"absolute",left:`${pct(cat.actual)}%`,top:"50%",transform:"translate(-50%,-50%)",zIndex:3}}>
          <div style={{width:18,height:18,borderRadius:"50%",background:color,border:`2px solid ${rgba(CREAM,0.3)}`,boxShadow:`0 0 8px ${rgba(color,0.5)}`}}/>
        </div>
        {cat.gap!==0&&(
          <svg style={{position:"absolute",top:"50%",transform:"translateY(-50%)",left:0,width:"100%",height:4,overflow:"visible",pointerEvents:"none"}}>
            <line x1={`${pct(cat.actual)}%`} y1="2" x2={`${pct(cat.desired)}%`} y2="2" stroke={isPos?rgba(SUCCESS,0.4):rgba(DANGER,0.4)} strokeWidth="1.5" strokeDasharray="3 2"/>
          </svg>
        )}
      </div>
      <div style={{display:"flex",justifyContent:"space-between",marginTop:6}}>
        <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
          <div style={{display:"flex",alignItems:"center",gap:5}}>
            <div style={{width:10,height:10,borderRadius:"50%",background:color,flexShrink:0}}/>
            <span style={{fontFamily:FONT,fontSize:10,color:rgba(CREAM,0.5)}}>{PRIOR_YEAR} actual: <strong style={{color:CREAM}}>{cat.actual}h</strong></span>
          </div>
          {cat.desired!==cat.actual&&(
            <div style={{display:"flex",alignItems:"center",gap:5}}>
              <div style={{width:10,height:10,borderRadius:"50%",border:`2px solid ${isPos?SUCCESS:DANGER}`,background:NAVY,flexShrink:0}}/>
              <span style={{fontFamily:FONT,fontSize:10,color:rgba(CREAM,0.5)}}>{CURRENT_YEAR} ideal: <strong style={{color:isPos?SUCCESS:DANGER}}>{cat.desired}h</strong></span>
            </div>
          )}
        </div>
        <div style={{display:"flex",alignItems:"center",gap:5,flexShrink:0}}>
          <div style={{width:2,height:10,background:GOLD,borderRadius:1}}/>
          <span style={{fontFamily:FONT,fontSize:10,color:rgba(GOLD,0.7)}}>{showNextTier&&nbm?"Next tier":"Peer"} avg: <strong style={{color:GOLD}}>{peerVal}h</strong></span>
        </div>
      </div>
    </div>
  );
}

const LEADERSHIP_ROLES = [
  { id: "coo",   label: "COO / Head of Operations"  },
  { id: "cfo",   label: "CFO / Finance"              },
  { id: "cro",   label: "CRO / Head of Sales"        },
  { id: "cmo",   label: "CMO / Marketing"            },
  { id: "cpto",  label: "CPO / Product & Technology" },
  { id: "chro",  label: "CHRO / People & HR"         },
];

const CONFIDENCE_SCORES = [
  { val: 1, label: "Significant concern" },
  { val: 2, label: "Notable concern"     },
  { val: 3, label: "Some concern"        },
  { val: 4, label: "Solid"               },
  { val: 5, label: "Fully confident"     },
];

function confidenceColor(n) {
  if (!n) return rgba(CREAM, 0.15);
  if (n >= 5) return SUCCESS;
  if (n === 4) return ON_COLOR;
  if (n === 3) return GOLD;
  if (n === 2) return "#D97706";
  return DANGER;
}

function StepLeadership({ data, onChange, onNext, onBack }) {
  const v = useFadeIn();
  return (
    <div style={{ opacity: v ? 1 : 0, transition: "opacity 0.35s ease" }}>
      <h2 style={{ fontFamily: FONT_SERIF, fontSize: 30, color: CREAM, marginBottom: 8, lineHeight: 1.2, fontWeight: 600 }}>
        Your Leadership Team
      </h2>
      <p style={{ color: rgba(CREAM, 0.5), marginBottom: 6, fontFamily: FONT, fontSize: 14, lineHeight: 1.6 }}>
        How confident are you that each key role has the right person for where you're taking the company?
      </p>
      <p style={{ color: rgba(CREAM, 0.3), marginBottom: 22, fontFamily: FONT, fontSize: 12 }}>
        Rate 1–5. Leave blank if the role is vacant or doesn't apply.
      </p>

      {/* Scale legend */}
      <div style={{ display: "flex", gap: 0, marginBottom: 16, borderRadius: 7, overflow: "hidden", border: `1px solid ${rgba(GOLD, 0.15)}` }}>
        {CONFIDENCE_SCORES.map((s, i) => (
          <div key={s.val} style={{ flex: 1, padding: "7px 4px", background: rgba(GOLD, i % 2 === 0 ? 0.05 : 0.02), textAlign: "center", borderRight: i < 4 ? `1px solid ${rgba(GOLD, 0.1)}` : "none" }}>
            <div style={{ fontFamily: FONT_SERIF, fontSize: 16, fontWeight: 600, color: confidenceColor(s.val), marginBottom: 2 }}>{s.val}</div>
            <div style={{ fontFamily: FONT, fontSize: 8, color: rgba(CREAM, 0.35), lineHeight: 1.3 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Role rows */}
      <div style={{ marginBottom: 24 }}>
        {LEADERSHIP_ROLES.map(role => {
          const sel = data[role.id] || null;
          return (
            <div key={role.id} style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 12, alignItems: "center", marginBottom: 8, padding: "11px 14px", background: rgba(GOLD, 0.03), borderRadius: 8, border: `1px solid ${rgba(sel ? confidenceColor(sel) : GOLD, sel ? 0.28 : 0.1)}`, borderLeft: `3px solid ${rgba(sel ? confidenceColor(sel) : GOLD, sel ? 0.65 : 0.2)}`, transition: "border-color 0.2s" }}>
              <div style={{ fontFamily: FONT, fontSize: 13, color: CREAM, fontWeight: 500 }}>{role.label}</div>
              <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                {CONFIDENCE_SCORES.map(s => {
                  const isSel = sel === s.val;
                  const col   = confidenceColor(s.val);
                  return (
                    <button
                      key={s.val}
                      onClick={() => onChange(role.id, isSel ? null : s.val)}
                      title={s.label}
                      style={{ width: 30, height: 30, borderRadius: "50%", border: `2px solid ${isSel ? col : rgba(CREAM, 0.15)}`, background: isSel ? col : rgba(GOLD, 0.04), cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s", flexShrink: 0 }}
                    >
                      <span style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, color: isSel ? NAVY : rgba(CREAM, 0.45) }}>{s.val}</span>
                    </button>
                  );
                })}
                {sel && (
                  <span style={{ fontFamily: FONT, fontSize: 10, color: confidenceColor(sel), marginLeft: 4, whiteSpace: "nowrap" }}>
                    {CONFIDENCE_SCORES.find(s => s.val === sel)?.label}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginBottom: 24, padding: "11px 16px", background: rgba(GOLD, 0.05), border: `1px solid ${rgba(GOLD, 0.15)}`, borderRadius: 7 }}>
        <div style={{ fontFamily: FONT, fontSize: 11, color: rgba(CREAM, 0.4), lineHeight: 1.6 }}>
          🔒 Your responses are strictly confidential and never shared with your team.
        </div>
      </div>

      <NavButtons onBack={onBack} onNext={onNext} disabled={false} nextLabel="See My Executive Summary →" />
    </div>
  );
}

function DebriefPreview({title,description,children}){
  return(
    <div style={{border:`1px solid ${rgba(GOLD,0.25)}`,borderRadius:10,overflow:"hidden",background:NAVY2}}>
      <div style={{background:rgba(GOLD,0.08),padding:"8px 14px",borderBottom:`1px solid ${rgba(GOLD,0.15)}`,display:"flex",alignItems:"center",gap:8}}>
        <div style={{display:"flex",gap:5}}>{["#C0392B","#E6A817","#27AE60"].map(c=><div key={c} style={{width:8,height:8,borderRadius:"50%",background:c,opacity:0.6}}/>)}</div>
        <div style={{flex:1,background:rgba(NAVY,0.6),borderRadius:3,padding:"3px 10px"}}><span style={{fontFamily:FONT,fontSize:10,color:rgba(CREAM,0.25)}}>CEO100 · Full Debrief Report</span></div>
      </div>
      <div style={{padding:"10px 14px 0"}}><div style={{display:"inline-flex",alignItems:"center",gap:6,background:rgba(GOLD,0.12),border:`1px solid ${rgba(GOLD,0.3)}`,padding:"3px 10px",borderRadius:3}}><span style={{fontFamily:FONT,fontSize:9,color:GOLD,letterSpacing:"0.15em",textTransform:"uppercase"}}>Included in your debrief</span></div></div>
      <div style={{padding:"12px 14px 16px",opacity:0.9}}>{children}</div>
      <div style={{padding:"10px 14px 14px",borderTop:`1px solid ${rgba(GOLD,0.1)}`}}>
        <div style={{fontFamily:FONT_SERIF,fontSize:15,color:CREAM,fontWeight:600,marginBottom:3}}>{title}</div>
        <div style={{fontFamily:FONT,fontSize:12,color:rgba(CREAM,0.4),lineHeight:1.5}}>{description}</div>
      </div>
    </div>
  );
}

// Wraps peer-benchmark content that should be visible in shape but not readable
// until the CEO books the debrief — blurs the children and overlays a lock badge
// with a short label explaining what's inside.
function LockedTeaser({children,label}){
  return(
    <div style={{position:"relative"}}>
      <div style={{filter:"blur(5px)",opacity:0.55,pointerEvents:"none",userSelect:"none"}} aria-hidden="true">{children}</div>
      <div style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center",padding:16}}>
        <div style={{background:rgba(NAVY,0.88),border:`1px solid ${rgba(GOLD,0.4)}`,borderRadius:10,padding:"14px 22px",textAlign:"center",maxWidth:320,boxShadow:`0 4px 30px ${rgba(NAVY,0.6)}`}}>
          <div style={{fontSize:18,marginBottom:6}}>🔒</div>
          <div style={{fontFamily:FONT,fontSize:12,color:CREAM,lineHeight:1.5,fontWeight:600}}>{label}</div>
        </div>
      </div>
    </div>
  );
}

function Results({profile,actual,desired,openSignals,leadership,onRestart,turnstileToken}){
  const v = useFadeIn();
  // Next-tier comparison is shown alongside peer comparison inside the locked
  // debrief sections rather than via a toggle — there's no free-view control
  // that would reveal peer/next-tier figures ahead of the debrief.
  const showNextTier = false;

  const [state, setState] = useState({submitting:true,succeeded:false,errors:[]});

  useEffect(() => {
    let cancelled = false;

    async function submitBenchmark(){
      setState({submitting:true,succeeded:false,errors:[]});
      try {
        const response = await fetch(BENCHMARK_API_URL, {
          method: "POST",
          headers: {"Content-Type":"application/json"},
          body: JSON.stringify({
            name:        profile.name,
            title:       profile.title,
            company:     profile.company,
            email:       profile.email,
            revenue:     profile.revenue,
            industry:    profile.industry,
            ownership:   profile.ownership || "",
            aiMaturity:  profile.aiMaturity || "",
            actual,
            desired,
            tooMuch:     openSignals.tooMuch   || "",
            tooLittle:   openSignals.tooLittle || "",
            leadership:  leadership || {},
            submittedAt: new Date().toISOString(),
            turnstileToken: turnstileToken || "",
          }),
        });

        const result = await response.json().catch(()=>({}));
        if(!response.ok || !result.ok){
          throw new Error(result.error || "Benchmark submission failed.");
        }
        if(!cancelled) setState({submitting:false,succeeded:true,errors:[]});
      } catch(error) {
        if(!cancelled) setState({submitting:false,succeeded:false,errors:[error]});
      }
    }

    submitBenchmark();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const bm  = BENCHMARKS[profile.revenue]||BENCHMARKS["$100M-$500M"];
  const nt  = getNextTier(profile.revenue);
  const nbm = nt ? BENCHMARKS[nt] : null;
  const inCats=CATEGORIES.filter(c=>c.inOn==="IN"), onCats=CATEGORIES.filter(c=>c.inOn==="ON");
  const myAI=sumHours(actual,"IN"), myAO=sumHours(actual,"ON");
  const myDI=sumHours(desired,"IN"), myDO=sumHours(desired,"ON");
  const myAT=sumHours(actual), myDT=sumHours(desired);
  const basePeerIn=parseFloat(inCats.reduce((s,c)=>s+(bm[c.id]||0),0).toFixed(1));
  const basePeerOn=parseFloat(onCats.reduce((s,c)=>s+(bm[c.id]||0),0).toFixed(1));
  const nextIn=nbm?parseFloat(inCats.reduce((s,c)=>s+(nbm[c.id]||0),0).toFixed(1)):null;
  const nextOn=nbm?parseFloat(onCats.reduce((s,c)=>s+(nbm[c.id]||0),0).toFixed(1)):null;
  const onShift=parseFloat((myDO-myAO).toFixed(1));
  const peerOnPct=Math.round((basePeerOn/bm.hoursPerWeek)*100);
  const myOnPct=myAT>0?Math.round((myAO/myAT)*100):0;
  const onDelta=myOnPct-peerOnPct;
  const boardA=parseFloat(actual?.board)||0;
  const allGaps=CATEGORIES.map(cat=>({...cat,actual:parseFloat(actual?.[cat.id])||0,desired:parseFloat(desired?.[cat.id])||0,peer:bm[cat.id]||0,gap:parseFloat(((desired?.[cat.id]||0)-(actual?.[cat.id]||0)).toFixed(1))})).sort((a,b)=>Math.abs(b.gap)-Math.abs(a.gap));
  const radarData=CATEGORIES.map(cat=>({category:cat.short,Actual:myAT>0?Math.round((parseFloat(actual?.[cat.id]||0)/myAT)*100):0,Ideal:myDT>0?Math.round((parseFloat(desired?.[cat.id]||0)/myDT)*100):0,Peers:Math.round(((bm[cat.id]||0)/bm.hoursPerWeek)*100)}));

  return(
    <div style={{maxWidth:920,margin:"0 auto",opacity:v?1:0,transition:"opacity 0.45s ease"}}>

      <div style={{marginBottom:24}}>
        {state.submitting&&(
          <div style={{padding:"14px 18px",background:rgba(GOLD,0.1),border:`1px solid ${rgba(GOLD,0.3)}`,borderRadius:8,display:"flex",alignItems:"center",gap:12}}>
            <div className="spinner"/>
            <span style={{fontFamily:FONT,fontSize:14,color:GOLD}}>Securely transmitting your benchmark data...</span>
          </div>
        )}
        {state.succeeded&&(
          <div style={{padding:"14px 18px",background:rgba(SUCCESS,0.12),border:`1px solid ${rgba(SUCCESS,0.4)}`,borderRadius:8,display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:18,color:SUCCESS}}>✓</span>
            <span style={{fontFamily:FONT,fontSize:14,color:rgba(CREAM,0.9)}}>
              Your results have been received. A CEO100 advisor will be in touch before your debrief call.
            </span>
          </div>
        )}
        {state.errors&&state.errors.length>0&&(
          <div style={{padding:"14px 18px",background:rgba(DANGER,0.15),border:`1px solid ${rgba(DANGER,0.4)}`,borderRadius:8,display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:18,color:DANGER}}>⚠</span>
            <span style={{fontFamily:FONT,fontSize:14,color:rgba(CREAM,0.9)}}>
              We encountered a connection issue. Please email <a href="mailto:info@chiefexec.com" style={{color:GOLD,fontWeight:600}}>info@chiefexecutivenetwork.com</a> to ensure your debrief is scheduled.
            </span>
          </div>
        )}
      </div>

      <div style={{textAlign:"center",marginBottom:28}}>
        <div style={{display:"inline-flex",alignItems:"center",gap:8,border:`1px solid ${rgba(GOLD,0.35)}`,padding:"5px 16px",borderRadius:3,marginBottom:16}}>
          <div style={{width:6,height:6,borderRadius:"50%",background:GOLD}}/>
          <span style={{fontFamily:FONT,fontSize:10,letterSpacing:"0.2em",textTransform:"uppercase",color:GOLD}}>CEO100 · Executive Summary</span>
        </div>
        <h1 style={{fontFamily:FONT_SERIF,fontSize:"clamp(26px,5vw,40px)",color:CREAM,marginBottom:8,lineHeight:1.1,fontWeight:600}}>{profile.name}'s Time Benchmark</h1>
        <p style={{color:rgba(CREAM,0.4),fontFamily:FONT,fontSize:13}}>
          {profile.title&&<>{profile.title} · </>}{profile.company} · {profile.revenue} · Benchmarked against CEO100 peers
        </p>
        <GoldRule my={20}/>
      </div>

      <InOnExplainer/>

      {/* Stat tile subs are directional only (above/below/in line) — the exact peer
          numbers behind them are reserved for the gated debrief content below. */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(150px,1fr))",gap:12,marginBottom:28}}>
        {[
          {label:"Your ON-Business Time",value:`${myOnPct}%`,sub:onDelta>3?"Above the typical pattern for your tier":onDelta<-3?"Below the typical pattern for your tier":"In line with the typical pattern",flag:Math.abs(onDelta)>6},
          {label:"Strategic Shift Desired",value:`${onShift>0?"+":""}${onShift}h/wk`,sub:onShift>0?"more ON time":"less ON time",flag:Math.abs(onShift)>3},
          {label:"Your Weekly Total",value:`${myAT}h`,sub:(myAT-bm.hoursPerWeek)>2?"Above the typical CEO workweek":(myAT-bm.hoursPerWeek)<-2?"Below the typical CEO workweek":"In line with the typical workweek",flag:false},
          {label:"Board & IR Hours",value:`${boardA}h/wk`,sub:(boardA-bm.board)>1?"Above the typical pattern for your tier":(boardA-bm.board)<-1?"Below the typical pattern for your tier":"In line with the typical pattern",flag:Math.abs(boardA-bm.board)>1.5},
        ].map(s=>(
          <div key={s.label} style={{background:rgba(GOLD,0.05),border:`1px solid ${rgba(GOLD,s.flag?0.4:0.12)}`,borderRadius:8,padding:"16px",textAlign:"center"}}>
            <div style={{fontFamily:FONT,fontSize:10,color:rgba(GOLD,0.55),textTransform:"uppercase",letterSpacing:"0.1em",marginBottom:8}}>{s.label}</div>
            <div style={{fontFamily:FONT_SERIF,fontSize:28,color:s.flag?GOLD:CREAM,fontWeight:600}}>{s.value}</div>
            <div style={{fontFamily:FONT,fontSize:11,color:rgba(CREAM,0.35),marginTop:4}}>{s.sub}</div>
          </div>
        ))}
      </div>

      <div style={{background:`linear-gradient(150deg,${NAVY} 0%,${NAVY3} 100%)`,borderRadius:14,padding:"24px 20px",marginBottom:10,border:`1px solid ${rgba(GOLD,0.2)}`}}>
        <div style={{marginBottom:16}}>
          <div style={{fontFamily:FONT,fontSize:9,letterSpacing:"0.2em",textTransform:"uppercase",color:rgba(GOLD,0.5),marginBottom:5}}>The Big Picture</div>
          <div style={{fontFamily:FONT_SERIF,fontSize:20,color:CREAM,fontWeight:600}}>Your Hours by Category</div>
          <div style={{fontFamily:FONT,fontSize:11,color:rgba(CREAM,0.35),marginTop:3}}>Your {PRIOR_YEAR} actual per category · full peer and next-tier comparison is in your debrief</div>
        </div>
        <div style={{height:1,background:rgba(GOLD,0.1),marginBottom:16}}/>
        <StackedPeerChart actual={actual} bm={bm} nbm={nbm} nt={nt} showNextTier={showNextTier} revealPeer={false}/>
      </div>

      <div style={{background:rgba(NAVY3,0.5),border:`1px solid ${rgba(GOLD,0.12)}`,borderRadius:10,padding:"18px 20px",marginBottom:28}}>
        <div style={{fontFamily:FONT,fontSize:9,color:rgba(GOLD,0.5),letterSpacing:"0.15em",textTransform:"uppercase",marginBottom:14}}>IN vs ON aggregate · hours per week</div>
        <SplitBarRow label="You — Last Year"    sublabel={`${PRIOR_YEAR} actual`}     inH={myAI} onH={myAO} highlight/>
        <SplitBarRow label="You — This Year"    sublabel={`${CURRENT_YEAR} ideal`}    inH={myDI} onH={myDO} aspirational/>
        <div style={{height:1,background:rgba(GOLD,0.08),margin:"8px 0"}}/>
        <LockedTeaser label="See how you compare to CEO100 peers and the next revenue tier in your debrief">
          <SplitBarRow label={`${profile.revenue} Peers`} sublabel="CEO100 benchmark"  inH={basePeerIn} onH={basePeerOn}/>
          {nbm&&nextIn!==null&&nextOn!==null&&<SplitBarRow label={`${nt} Level`} sublabel="aspirational" inH={nextIn} onH={nextOn} aspirational/>}
        </LockedTeaser>
      </div>

      <div style={{marginBottom:10}}>
        <div style={{marginBottom:8}}>
          <h2 style={{fontFamily:FONT_SERIF,fontSize:26,color:CREAM,fontWeight:600,marginBottom:2}}>Your Priority Gaps</h2>
          <p style={{fontFamily:FONT,fontSize:13,color:rgba(CREAM,0.4),lineHeight:1.5}}>Where your actual and ideal allocations diverge most.</p>
        </div>
        {allGaps.length>0&&allGaps[0].gap!==0&&(
          <div style={{marginBottom:14,padding:"13px 16px",background:rgba(GOLD,0.06),border:`1px solid ${rgba(GOLD,0.25)}`,borderRadius:8}}>
            <span style={{fontFamily:FONT,fontSize:13,color:CREAM}}>Your biggest planned shift: <strong style={{color:GOLD}}>{allGaps[0].gap>0?"more":"less"} time on {allGaps[0].label}</strong></span>
          </div>
        )}
        <LockedTeaser label="Unlock your full 9-category priority-gap analysis — mapped against your peer cohort and the next revenue tier — in your debrief">
          {allGaps.map(g=><GapDotRow key={g.id} cat={g} showNextTier={showNextTier} bm={bm} nbm={nbm}/>)}
          {boardA>0&&(
            <div style={{marginTop:14,background:rgba(ON_COLOR,0.07),border:`1px solid ${rgba(ON_COLOR,0.25)}`,borderRadius:8,padding:"14px 18px"}}>
              <div style={{fontFamily:FONT,fontSize:9,letterSpacing:"0.15em",textTransform:"uppercase",color:ON_COLOR,marginBottom:8}}>Scale Signal · Board & Investor Relations</div>
              <p style={{fontFamily:FONT,fontSize:13,color:rgba(CREAM,0.6),lineHeight:1.65,margin:0}}>
                You spend <strong style={{color:CREAM}}>{boardA} hrs/week</strong> on board and investor relations — peer average is <strong style={{color:CREAM}}>{bm.board} hrs</strong>.
                {nbm&&<> At <strong style={{color:CREAM}}>{nt}</strong>, the average is <strong style={{color:CREAM}}>{nbm.board} hrs/week</strong> — the single most consistent indicator of scale focus among CEO100 members.</>}
              </p>
            </div>
          )}
        </LockedTeaser>
      </div>

      <GoldRule my={40}/>
      <div style={{textAlign:"center",marginBottom:28}}>
        <div style={{display:"inline-flex",alignItems:"center",gap:8,background:rgba(GOLD,0.08),border:`1px solid ${rgba(GOLD,0.3)}`,padding:"5px 16px",borderRadius:3,marginBottom:14}}>
          <span style={{fontSize:12}}>✦</span>
          <span style={{fontFamily:FONT,fontSize:10,letterSpacing:"0.2em",textTransform:"uppercase",color:GOLD}}>Included in Your 20-Minute Debrief</span>
        </div>
        <h2 style={{fontFamily:FONT_SERIF,fontSize:"clamp(20px,4vw,32px)",color:CREAM,marginBottom:10,lineHeight:1.2,fontWeight:600}}>There's significantly more in your full analysis</h2>
        <p style={{fontFamily:FONT,fontSize:14,color:rgba(CREAM,0.45),maxWidth:520,margin:"0 auto",lineHeight:1.7}}>A CEO100 advisor will walk you through your complete report in a private 20-minute conversation — tailored to your data, your industry, and your peer cohort.</p>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))",gap:16,marginBottom:16}}>
        <DebriefPreview title="Full Allocation Radar vs. CEO100 Peers" description="9-category radar overlaid against peers and the next revenue tier.">
          <LockedTeaser label="Unlocked in your 20-minute debrief">
            <div style={{height:195}}><ResponsiveContainer width="100%" height="100%"><RadarChart data={radarData} margin={{top:8,right:30,bottom:8,left:30}}><PolarGrid stroke={rgba(GOLD,0.15)}/><PolarAngleAxis dataKey="category" tick={{fontFamily:FONT,fontSize:9,fill:rgba(CREAM,0.45)}}/><Radar name="Actual" dataKey="Actual" stroke={GOLD} fill={GOLD} fillOpacity={0.15} strokeWidth={2}/><Radar name="Ideal" dataKey="Ideal" stroke={ON_COLOR} fill={ON_COLOR} fillOpacity={0.08} strokeWidth={1.5} strokeDasharray="4 2"/><Radar name="Peers" dataKey="Peers" stroke={rgba(CREAM,0.3)} fill="none" strokeWidth={1} strokeDasharray="2 3"/></RadarChart></ResponsiveContainer></div>
          </LockedTeaser>
        </DebriefPreview>
        <DebriefPreview title="Hours, Scale & Capacity Analysis" description="How total hours and IN/ON balance shift across the $100M–$1B growth curve.">
          <LockedTeaser label="Unlocked in your 20-minute debrief">
            <div style={{height:135,marginBottom:8}}><ResponsiveContainer width="100%" height="100%"><BarChart data={REVENUE_TIERS.map(t=>({tier:t.replace("$","").replace("M-$","–"),hrs:BENCHMARKS[t].hoursPerWeek}))} margin={{top:0,right:0,bottom:0,left:-24}}><XAxis dataKey="tier" tick={{fontFamily:FONT,fontSize:9,fill:rgba(CREAM,0.3)}} axisLine={false} tickLine={false}/><YAxis tick={{fontFamily:FONT,fontSize:9,fill:rgba(CREAM,0.25)}} axisLine={false} tickLine={false} domain={[55,62]}/><Tooltip formatter={v=>[`${v} hrs/wk`]} contentStyle={{background:NAVY2,border:`1px solid ${rgba(GOLD,0.3)}`,fontFamily:FONT,fontSize:11,color:CREAM,borderRadius:6}}/><Bar dataKey="hrs" radius={[3,3,0,0]}>{REVENUE_TIERS.map((t,i)=><Cell key={i} fill={t===profile.revenue?GOLD:rgba(GOLD,0.22)}/>)}</Bar></BarChart></ResponsiveContainer></div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>{[{label:"Your Total",val:`${myAT}h`,sub:"hrs/wk"},{label:"Peer Avg",val:`${bm.hoursPerWeek}h`,sub:"CEO100"},{label:"Next Tier",val:nbm?`${nbm.hoursPerWeek}h`:"—",sub:nt||"top"}].map(s=>(<div key={s.label} style={{background:rgba(GOLD,0.05),borderRadius:5,padding:"8px 10px",textAlign:"center"}}><div style={{fontFamily:FONT,fontSize:9,color:rgba(GOLD,0.5),marginBottom:4,textTransform:"uppercase",letterSpacing:"0.08em"}}>{s.label}</div><div style={{fontFamily:FONT_SERIF,fontSize:20,color:CREAM,fontWeight:600}}>{s.val}</div><div style={{fontFamily:FONT,fontSize:9,color:rgba(CREAM,0.3)}}>{s.sub}</div></div>))}</div>
          </LockedTeaser>
        </DebriefPreview>
      </div>

      {/* Booking CTA — this section was previously commented out with unfinished
          placeholder styles, leaving the results page with no way for a CEO to
          self-serve a booking. Rebuilt below with real styles. CALENDLY_URL at
          the top of this file is a placeholder — swap in the real scheduling
          link before merging. */}
      <div style={{marginTop:40,background:`linear-gradient(150deg,${NAVY} 0%,${NAVY3} 100%)`,border:`1px solid ${rgba(GOLD,0.35)}`,borderRadius:14,padding:"40px 28px",textAlign:"center"}}>
        <div style={{display:"inline-flex",alignItems:"center",gap:8,background:rgba(GOLD,0.1),border:`1px solid ${rgba(GOLD,0.3)}`,padding:"5px 16px",borderRadius:3,marginBottom:18}}>
          <span style={{fontSize:12}}>✦</span>
          <span style={{fontFamily:FONT,fontSize:10,letterSpacing:"0.2em",textTransform:"uppercase",color:GOLD}}>Chief Executive Network · CEO100</span>
        </div>
        <h3 style={{fontFamily:FONT_SERIF,fontSize:"clamp(22px,4vw,30px)",color:CREAM,fontWeight:600,marginBottom:10,lineHeight:1.2}}>Schedule Your Private 20-Minute Debrief</h3>
        <p style={{fontFamily:FONT,fontSize:14,color:rgba(CREAM,0.5),maxWidth:460,margin:"0 auto 26px",lineHeight:1.7}}>A CEO100 advisor will personally walk you through your complete report — peer benchmarks, priority gaps, and what CEOs at the next revenue tier do differently.</p>
        <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer"
          style={{display:"inline-block",padding:"15px 36px",background:`linear-gradient(135deg,${GOLD2},${GOLD})`,color:NAVY,borderRadius:6,fontFamily:FONT,fontSize:14,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase",textDecoration:"none",boxShadow:`0 4px 24px ${rgba(GOLD,0.3)}`}}>
          Book My Debrief Call →
        </a>
        <div style={{display:"flex",flexWrap:"wrap",justifyContent:"center",gap:18,marginTop:22}}>
          {["Strictly confidential","No commitment","20 minutes · your data · your peers"].map(t=>(
            <div key={t} style={{display:"flex",alignItems:"center",gap:6}}>
              <div style={{width:5,height:5,borderRadius:"50%",background:GOLD,opacity:0.7}}/>
              <span style={{fontFamily:FONT,fontSize:11,color:rgba(CREAM,0.4)}}>{t}</span>
            </div>
          ))}
        </div>
        <div style={{textAlign:"center",marginTop:36,paddingBottom:8}}>
          <button onClick={onRestart}
            style={{background:"none",border:"none",cursor:"pointer",fontFamily:FONT,fontSize:12,color:rgba(CREAM,0.25),textDecoration:"underline",padding:0}}>
            ← Edit my responses and retake
          </button>
        </div>
      </div>
    </div>
  );
}

export default function App(){
  const [step,setStep]               = useState(1);
  const [profile,setProfile]         = useState({name:"",company:"",revenue:"",industry:"",title:"",email:"",ownership:"",aiMaturity:""});
  const [actual,setActual]           = useState({});
  const [desired,setDesired]         = useState({});
  const [openSignals,setOpenSignals] = useState({tooMuch:"",tooLittle:""});
  const [leadership,setLeadership]   = useState({});
  const [turnstileToken,setTurnstileToken] = useState(null);
  const [pendingResults,setPendingResults] = useState(false);
  const turnstileWidgetId = useRef(null);

  useEffect(()=>{
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if(!window.turnstile) return;
      turnstileWidgetId.current = window.turnstile.render("#turnstile-container", {
        sitekey: CF_TURNSTILE_SITE_KEY,
        size: "invisible",
        callback: (token) => { setTurnstileToken(token); },
        "error-callback": () => { setTurnstileToken(null); setPendingResults(false); },
        "expired-callback": () => { setTurnstileToken(null); },
      });
    };
    document.head.appendChild(script);
    return () => { try { document.head.removeChild(script); } catch(_){} };
  }, []);

  useEffect(()=>{
    if(pendingResults && turnstileToken){
      setPendingResults(false);
      setStep(6);
    }
  }, [turnstileToken, pendingResults]);

  const handleOpenSignalsNext = () => {
    setStep(5); 
  };

  const handleLeadershipNext = () => {
    if(window.turnstile && turnstileWidgetId.current !== null && !turnstileToken){
      setPendingResults(true);
      window.turnstile.execute(turnstileWidgetId.current);
    } else {
      setStep(6); // Turnstile already resolved or not available
    }
  };

  const TOTAL_STEPS = 5;
  const stepLabels  = ["","Tell Us About You",`Your ${PRIOR_YEAR} Allocation`,`Your ${CURRENT_YEAR} Ideal`,"Before Your Results","Your Leadership Team"];

  const handleActualNext = () => {
    // NOTE: previously pre-filled the "ideal" step with the CEO's actual-hours
    // answers, anchoring their ideal allocation to what they'd just entered.
    // Step 3 now starts blank so the ideal allocation is an independent answer.
    setStep(3);
  };

  useEffect(()=>{
    if(step===1||step===5) return;
    const handler = e => { e.preventDefault(); e.returnValue=""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  },[step]);

  const handleRestart = () => {
    setStep(1);
    setProfile({name:"",company:"",revenue:"",industry:"",title:"",email:"",ownership:"",aiMaturity:""});
    setActual({});
    setDesired({});
    setOpenSignals({tooMuch:"",tooLittle:""});
    setLeadership({});
    setTurnstileToken(null);
    try {
      if(window.turnstile && turnstileWidgetId.current !== null) window.turnstile.reset(turnstileWidgetId.current);
    } catch(_) {}
    window.scrollTo(0,0);
  };

  return (
      <div style={{ minHeight: "100vh", background: NAVY }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=Open+Sans:wght@400;500;600;700&display=swap');
          
          /* New Loading Spinner Styles */
          @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          .spinner {
            width: 18px;
            height: 18px;
            border: 2px solid ${rgba(GOLD, 0.2)};
            border-top: 2px solid ${GOLD};
            border-radius: 50%;
            animation: spin 0.8s linear infinite;
          }

          * { box-sizing:border-box; margin:0; padding:0; }
          input[type=number]::-webkit-inner-spin-button { opacity:1; }
          input::placeholder, textarea::placeholder { color:rgba(249,245,238,0.25)!important; }
          textarea { line-height:1.5; }
          select option { background:#152238; color:#F9F5EE; }
          input[type=text], input[type=email], input[type=tel], input[type=number], textarea { color-scheme:dark; }
          @media(max-width:480px){
            input[type=number]{ width:52px!important; font-size:13px!important; }
          }

          /* Step 1: entry fields left, testimonials right (supporting) on
             wide screens; single column (fields first) below 760px. */
          .step1-grid { grid-template-columns: minmax(0,1.35fr) minmax(240px,1fr); }
          @media(max-width:760px){
            .step1-grid { grid-template-columns: 1fr; }
          }
        `}</style>

        <div style={{background:rgba(NAVY,0.95),borderBottom:`1px solid ${rgba(GOLD,0.18)}`,padding:"13px 24px",display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,zIndex:10,backdropFilter:"blur(8px)"}}>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <a href="https://chiefexecutivenetwork.com/ceo100" target="_blank" rel="noopener noreferrer"><img src="/images/ceo100-logo.png" alt="CEO100" style={{height:34,width:"auto",display:"block",flexShrink:0}}/></a>
            <div>
              <div style={{fontFamily:FONT_SERIF,fontSize:15,fontWeight:600,color:CREAM,letterSpacing:"0.02em"}}>Chief Executive Network</div>
              <div style={{fontFamily:FONT,fontSize:10,color:rgba(GOLD,0.6),letterSpacing:"0.12em",textTransform:"uppercase"}}>CEO100 Benchmark</div>
            </div>
          </div>
        </div>

        <div style={{maxWidth:(step===6||step===1)?960:620,margin:"0 auto",padding:"40px 16px 100px"}}>
          {step<6&&(
            <div style={{marginBottom:30}}>
              <ProgressBar step={step} total={TOTAL_STEPS}/>
              <StepLabel step={step} total={TOTAL_STEPS} label={stepLabels[step]}/>
            </div>
          )}
          <div id="turnstile-container" style={{display:"none"}}/>
          {step===1&&<StepProfile    data={profile}      onChange={(k,v)=>setProfile(p=>({...p,[k]:v}))}        onNext={()=>setStep(2)}/>}
          {step===2&&<StepActual     data={actual}        onChange={(id,v)=>setActual(p=>({...p,[id]:v}))}       onNext={handleActualNext}        onBack={()=>setStep(1)} profile={profile}/>}
          {step===3&&<StepDesired    data={desired}  onChange={(id,v)=>setDesired(p=>({...p,[id]:v}))} onNext={()=>setStep(4)} onBack={()=>setStep(2)} profile={profile}/>}
          {step===4&&<StepOpenSignals data={openSignals}  onChange={(k,v)=>setOpenSignals(p=>({...p,[k]:v}))}   onNext={handleOpenSignalsNext}   onBack={()=>setStep(3)}/>}
          {step===5&&<StepLeadership  data={leadership}   onChange={(k,v)=>setLeadership(p=>({...p,[k]:v}))}    onNext={handleLeadershipNext}    onBack={()=>setStep(4)}/>}
          {step===6&&<Results profile={profile} actual={actual} desired={desired} openSignals={openSignals} leadership={leadership} onRestart={handleRestart} turnstileToken={turnstileToken}/>}
        </div>
      </div>
    );
}
